/**
 * Shopify Responsive Navigation JavaScript
 * Handles mobile sidebar toggle and submenu expansion
 */

class ShopifyNavigation {
  constructor() {
    // Elements
    this.nav = document.querySelector('[data-navigation]');
    this.sidebar = document.querySelector('[data-nav-sidebar]');
    this.overlay = document.querySelector('[data-nav-overlay]');
    this.toggleBtn = document.querySelector('[data-nav-toggle]');
    this.closeBtn = document.querySelector('[data-nav-close]');
    this.expandBtns = document.querySelectorAll('[data-nav-expand]');

    // State
    this.isOpen = false;
    this.focusableElements = [];

    // Initialize
    this.init();
  }

  init() {
    if (!this.sidebar) return;

    // Event listeners
    this.toggleBtn?.addEventListener('click', () => this.openSidebar());
    this.closeBtn?.addEventListener('click', () => this.closeSidebar());
    this.overlay?.addEventListener('click', () => this.closeSidebar());

    // Expand/collapse buttons
    this.expandBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.toggleSubmenu(e));
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => this.handleKeyboard(e));

    // Handle cart count updates
    this.updateCartCount();

    // Close sidebar on window resize to desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1024 && this.isOpen) {
        this.closeSidebar();
      }
    });

    console.log('✅ Shopify Navigation initialized');
  }

  /**
   * Open mobile sidebar
   */
  openSidebar() {
    this.isOpen = true;
    this.sidebar.classList.add('is-open');
    this.overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';

    // Store focusable elements for keyboard navigation
    this.storeFocusableElements();

    // Focus first element
    setTimeout(() => {
      this.closeBtn?.focus();
    }, 300);

    // Announce to screen readers
    this.sidebar.setAttribute('aria-hidden', 'false');
  }

  /**
   * Close mobile sidebar
   */
  closeSidebar() {
    this.isOpen = false;
    this.sidebar.classList.remove('is-open');
    this.overlay.classList.remove('is-visible');
    document.body.style.overflow = '';

    // Return focus to toggle button
    setTimeout(() => {
      this.toggleBtn?.focus();
    }, 300);

    // Announce to screen readers
    this.sidebar.setAttribute('aria-hidden', 'true');
  }

  /**
   * Toggle submenu expansion
   */
  toggleSubmenu(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const linkWrapper = button.closest('.nav-mobile-link-wrapper');
    const parentItem = linkWrapper.closest('.nav-mobile-item, .nav-mobile-subitem');
    const submenu = parentItem.querySelector(':scope > .nav-mobile-submenu');

    if (!submenu) return;

    const isOpen = submenu.classList.contains('is-open');

    if (isOpen) {
      // Close submenu
      submenu.classList.remove('is-open');
      button.classList.remove('is-active');
      button.setAttribute('aria-expanded', 'false');

      // Also close all nested submenus
      const nestedSubmenus = submenu.querySelectorAll('.nav-mobile-submenu');
      nestedSubmenus.forEach(nested => {
        nested.classList.remove('is-open');
        const nestedBtn = nested.closest('.nav-mobile-item, .nav-mobile-subitem')
          ?.querySelector('.nav-expand');
        if (nestedBtn) {
          nestedBtn.classList.remove('is-active');
          nestedBtn.setAttribute('aria-expanded', 'false');
        }
      });
    } else {
      // Close siblings at the same level
      const siblings = parentItem.parentElement.children;
      Array.from(siblings).forEach(sibling => {
        if (sibling !== parentItem) {
          const siblingSubmenu = sibling.querySelector(':scope > .nav-mobile-submenu');
          const siblingBtn = sibling.querySelector(':scope > .nav-mobile-link-wrapper > .nav-expand');
          if (siblingSubmenu && siblingBtn) {
            siblingSubmenu.classList.remove('is-open');
            siblingBtn.classList.remove('is-active');
            siblingBtn.setAttribute('aria-expanded', 'false');
          }
        }
      });

      // Open submenu
      submenu.classList.add('is-open');
      button.classList.add('is-active');
      button.setAttribute('aria-expanded', 'true');
    }

    // Add smooth scroll animation
    this.smoothScrollToItem(parentItem);
  }

  /**
   * Smooth scroll to expanded item
   */
  smoothScrollToItem(item) {
    const sidebarContent = this.sidebar.querySelector('.nav-sidebar-content');
    const itemTop = item.offsetTop;
    const contentScrollTop = sidebarContent.scrollTop;
    const contentHeight = sidebarContent.clientHeight;

    // Only scroll if item is not fully visible
    if (itemTop < contentScrollTop || itemTop > contentScrollTop + contentHeight - 100) {
      sidebarContent.scrollTo({
        top: itemTop - 20,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Handle keyboard navigation
   */
  handleKeyboard(event) {
    // ESC key closes sidebar
    if (event.key === 'Escape' && this.isOpen) {
      this.closeSidebar();
    }

    // Tab trap when sidebar is open
    if (event.key === 'Tab' && this.isOpen) {
      this.handleTabTrap(event);
    }
  }

  /**
   * Trap focus inside sidebar
   */
  handleTabTrap(event) {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * Store focusable elements in sidebar
   */
  storeFocusableElements() {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    this.focusableElements = Array.from(
      this.sidebar.querySelectorAll(focusableSelectors)
    );
  }

  /**
   * Update cart count (integrate with Shopify cart)
   */
  updateCartCount() {
    const cartCountEl = document.querySelector('[data-cart-count]');
    if (!cartCountEl) return;

    // Fetch cart data
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        cartCountEl.textContent = cart.item_count;

        // Animate on update
        cartCountEl.style.transform = 'scale(1.2)';
        setTimeout(() => {
          cartCountEl.style.transform = 'scale(1)';
        }, 200);
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
      });
  }

  /**
   * Public method to refresh cart count
   */
  refreshCart() {
    this.updateCartCount();
  }
}

// Initialize navigation when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.shopifyNav = new ShopifyNavigation();
  });
} else {
  window.shopifyNav = new ShopifyNavigation();
}

// Listen for Shopify cart updates
document.addEventListener('cart:updated', () => {
  window.shopifyNav?.refreshCart();
});

// Handle cart form submissions
document.addEventListener('submit', (event) => {
  if (event.target.matches('form[action="/cart/add"]')) {
    event.preventDefault();

    const formData = new FormData(event.target);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
      .then(response => response.json())
      .then(data => {
        // Refresh cart count
        window.shopifyNav?.refreshCart();

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

        // Optional: Show notification
        console.log('✅ Product added to cart');
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
      });
  }
});

/**
 * Utility function: Close all mega menus on click outside
 */
document.addEventListener('click', (event) => {
  const isDesktop = window.innerWidth >= 1024;
  if (!isDesktop) return;

  const megaMenus = document.querySelectorAll('.mega-menu');
  const navItems = document.querySelectorAll('.nav-item');

  // Check if click is outside all nav items
  const isOutside = !event.target.closest('.nav-item');

  if (isOutside) {
    navItems.forEach(item => {
      item.classList.remove('is-active');
    });
  }
});

/**
 * Performance optimization: Debounce resize events
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Optimize resize handler
const handleResize = debounce(() => {
  if (window.innerWidth >= 1024 && window.shopifyNav?.isOpen) {
    window.shopifyNav.closeSidebar();
  }
}, 250);

window.addEventListener('resize', handleResize);

/**
 * Accessibility: Announce navigation changes to screen readers
 */
function announceToScreenReader(message) {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', 'polite');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  document.body.appendChild(announcement);

  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}

// Add screen reader only class
const style = document.createElement('style');
style.textContent = `
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
`;
document.head.appendChild(style);
